import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, ChevronDown, Loader2, Network, Pencil, Trash2 } from 'lucide-react';
import { IconCopyCheck, IconPlus, IconTransferVertical } from '@tabler/icons-react';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import { technicianInitials } from '../../board/utils/technicianInitials';
import { Ci2HelpHexIcon } from '../../process-config/components/Ci2HelpHexIcon';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/board-search-filters/BoardSearchFilters.css';
import '../../process-config/ConfigStatusView2.css';
import '../../process-config/components/CondicoesIntervencaoTableV2.css';
import {
  Ci2EditPopover,
  Ci2EditPopoverActions,
} from '../../process-config/components/Ci2EditPopover';
import {
  assignChatTeamAgents,
  createChatAgent,
  createChatTeam,
  deleteChatAgent,
  deleteChatTeam,
  fetchChatAgents,
  fetchChatEligibleUsers,
  fetchChatPreAtendimento,
  fetchChatTeamAgents,
  fetchChatTeams,
  updateChatAgentCodtech,
  updateChatTeam,
} from '../services/customersChatService';
import { GoUsrCodePicker } from '../../go-config/components/GoUsrCodePills';
import {
  goCodeKey,
  isGoCodeTaken,
  isValidGoCode,
} from '../../go-config/utils/goUserCodeRules';
import '../../go-config/components/GoUsuariosConfig.css';
import { ROLE_LABELS } from '../utils/customersChatUtils';
import { useUserProfilePhotos } from '../../user-management/hooks/useUserProfilePhotos';

const TEAM_DOT_COLORS = ['#6e56cf', '#265ed4', '#0e8a82', '#17845a', '#b4670e', '#767889'];
const ATIVOS_KEY = 'usuarios-ativos';
const SEM_TIME_KEY = 'sem-time';
const SEM_TIME_COLOR = '#767889';
const CHAT_ONLY_HELP = 'Ao abrir o sistema, visualizar somente a tela de chat';
const TABLE_COLSPAN = 8;
const ROLE_ITEMS = [
  { value: 'agent', label: 'Agente' },
  { value: 'administrator', label: 'Administrador' },
];
const POPOVER_CLASS =
  'board-search-filters-popover board-search-filters-popover--simple board-search-filters-popover--tecnico ec-module-popover';

function agentId(agent) {
  const n = Number(agent?.id ?? agent?.user_id);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function agentName(agent) {
  return (
    String(agent?.name || agent?.available_name || agent?.displayName || '').trim() || '—'
  );
}

function agentEmail(agent) {
  return String(agent?.email || agent?.loginEmail || '').trim() || '—';
}

function agentVerveUserId(agent) {
  const n = Number(agent?.verveUserId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function agentRole(agent) {
  const role = agent?.role || agent?.linkedRole || agent?.role_name;
  return ROLE_LABELS[role] || role || '—';
}

function Ci2Switch({ checked, disabled, onChange, tooltip }) {
  const switchBtn = (
    <button
      type="button"
      className={`ci2-switch${checked ? ' on' : ''}`}
      disabled={disabled}
      aria-pressed={checked}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled && onChange) onChange(!checked);
      }}
    />
  );
  if (!tooltip) return switchBtn;
  return (
    <span className="ci2-switch-tooltip-wrap ci2-modern-tooltip-host" data-tooltip={tooltip}>
      {switchBtn}
    </span>
  );
}

async function fetchUsersChatOnlyMap() {
  const token = localStorage.getItem('authToken');
  const res = await fetch('/api/users', {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const json = await res.json().catch(() => ({}));
  const map = new Map();
  const list = Array.isArray(json?.users)
    ? json.users
    : Array.isArray(json?.data)
      ? json.data
      : Array.isArray(json)
        ? json
        : [];
  list.forEach((u) => {
    const id = Number(u?.id);
    if (!Number.isFinite(id) || id <= 0) return;
    map.set(
      id,
      u.customers_chat_only === true || u.customers_chat_only === 1 || u.customers_chat_only === '1'
    );
  });
  return map;
}

async function setUserChatOnly(userId, nextOnly) {
  const token = localStorage.getItem('authToken');
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'Content-Type': 'application/json',
  };
  const getRes = await fetch(`/api/users/${userId}/config`, { headers });
  const cfg = await getRes.json().catch(() => ({}));
  if (!getRes.ok || cfg.success === false) {
    throw new Error(cfg.message || 'Erro ao carregar configuração do usuário');
  }
  const postRes = await fetch(`/api/users/${userId}/config`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      asstec_showos: cfg.asstec_showos ?? 0,
      asstec_openos: cfg.asstec_openos ?? 0,
      asstec_closeos: cfg.asstec_closeos ?? 0,
      asstec_deleteos: cfg.asstec_deleteos ?? 0,
      asstec_create_reports: cfg.asstec_create_reports ?? 0,
      customers_chat_only: nextOnly ? 1 : 0,
    }),
  });
  const postJson = await postRes.json().catch(() => ({}));
  if (!postRes.ok || postJson.success === false) {
    throw new Error(postJson.message || 'Erro ao salvar somente chat');
  }
}

function agentPhoto(agent, photoMap) {
  const verveId = Number(agent?.verveUserId || agent?.id);
  if (Number.isFinite(verveId) && verveId > 0 && photoMap) {
    const mapped = photoMap[String(verveId)];
    if (mapped) return String(mapped).trim();
  }
  return String(agent?.thumbnail || agent?.avatar_url || agent?.avatarUrl || '').trim();
}

function sameAgentRef(a, b) {
  if (!a || !b) return false;
  const aId = agentId(a);
  const bId = agentId(b);
  if (aId != null && bId != null) return aId === bId;
  const aLink = Number(a.linkId);
  const bLink = Number(b.linkId);
  if (Number.isFinite(aLink) && aLink > 0 && Number.isFinite(bLink) && bLink > 0) {
    return aLink === bLink;
  }
  const aVerve = agentVerveUserId(a);
  const bVerve = agentVerveUserId(b);
  return aVerve != null && bVerve != null && aVerve === bVerve;
}

function VchConfirmModal({
  titleId = 'vch-confirm-title',
  title,
  message,
  confirmLabel = 'Confirmar',
  confirmingLabel = 'Aguarde…',
  confirming = false,
  onConfirm,
  onCancel,
}) {
  return ReactDOM.createPortal(
    <div
      className="vch-confirm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !confirming) onCancel?.();
      }}
    >
      <div className="vch-confirm-modal">
        <h3 id={titleId} className="vch-confirm-title">
          {title}
        </h3>
        <div className="vch-confirm-message">{message}</div>
        <div className="vch-confirm-actions">
          <button
            type="button"
            className="user-form-link-btn user-form-footer-btn"
            onClick={onCancel}
            disabled={confirming}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="user-form-profile-save-btn user-form-footer-btn"
            onClick={onConfirm}
            disabled={confirming}
          >
            {confirming ? confirmingLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function AgentAvatar({ person, size = 22, photoMap }) {
  const photo = agentPhoto(person, photoMap);
  const label = agentName(person);
  if (photo) {
    return (
      <span
        className="vch-user-picker-avatar vch-user-picker-avatar--photo vch-agents-teams-avatar"
        style={{ width: size, height: size }}
        aria-hidden
      >
        <img src={photo} alt="" className="vch-user-picker-avatar-img" />
      </span>
    );
  }
  return (
    <span
      className="vch-user-picker-avatar vch-user-picker-avatar--initials vch-agents-teams-avatar"
      style={{ width: size, height: size, fontSize: Math.max(8, Math.round(size * 0.38)) }}
      aria-hidden
    >
      {technicianInitials(label === '—' ? '' : label)}
    </span>
  );
}

function RolePickerPopover({ anchorRect, onPick, onClose }) {
  const style = useMemo(() => {
    if (!anchorRect) return null;
    const width = 200;
    const margin = 8;
    let left = anchorRect.left;
    if (left + width > window.innerWidth - margin) {
      left = window.innerWidth - width - margin;
    }
    left = Math.max(margin, left);
    const top = anchorRect.bottom + 4;
    return {
      position: 'fixed',
      top,
      left,
      width,
      zIndex: 10050,
      boxSizing: 'border-box',
      maxHeight: 160,
      '--bsf-cidade-popover-max-h': '160px',
    };
  }, [anchorRect]);

  useEffect(() => {
    if (!anchorRect) return undefined;
    const onDoc = (e) => {
      if (e.target?.closest?.('[data-vch-role-picker]')) return;
      onClose?.();
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [anchorRect, onClose]);

  if (!anchorRect || !style) return null;

  return ReactDOM.createPortal(
    <div
      data-vch-role-picker
      className={POPOVER_CLASS}
      style={style}
      role="dialog"
      aria-label="Papel do agente"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <BoardSearchSimplePickerPanel
        showSearch={false}
        items={ROLE_ITEMS}
        getKey={(item) => item.value}
        getLabel={(item) => item.label}
        selectedKeys={[]}
        onToggle={(item) => onPick?.(item.value)}
        emptyMessage="Nenhuma opção"
        listAriaLabel="Papel do agente"
      />
    </div>,
    document.body
  );
}

function TeamPickerPopover({
  anchorRect,
  items,
  onPick,
  onClose,
  ariaLabel = 'Selecionar time',
  emptyMessage = 'Nenhum time disponível',
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const filteredItems = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => String(item.label || '').toLowerCase().includes(q));
  }, [items, searchTerm]);

  const style = useMemo(() => {
    if (!anchorRect) return null;
    const width = 220;
    const margin = 8;
    let left = anchorRect.left;
    if (left + width > window.innerWidth - margin) {
      left = window.innerWidth - width - margin;
    }
    left = Math.max(margin, left);
    const top = anchorRect.bottom + 4;
    return {
      position: 'fixed',
      top,
      left,
      width,
      zIndex: 10050,
      boxSizing: 'border-box',
      maxHeight: 240,
      '--bsf-cidade-popover-max-h': '240px',
    };
  }, [anchorRect]);

  useEffect(() => {
    if (!anchorRect) return undefined;
    setSearchTerm('');
    const onDoc = (e) => {
      if (e.target?.closest?.('[data-vch-team-picker]')) return;
      onClose?.();
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [anchorRect, onClose]);

  if (!anchorRect || !style) return null;

  return ReactDOM.createPortal(
    <div
      data-vch-team-picker
      className={POPOVER_CLASS}
      style={style}
      role="dialog"
      aria-label={ariaLabel}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <BoardSearchSimplePickerPanel
        showSearch={items.length > 5}
        searchPlaceholder="Buscar time"
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        items={filteredItems}
        getKey={(item) => item.id}
        getLabel={(item) => item.label}
        selectedKeys={[]}
        onToggle={(item) => onPick?.(item)}
        emptyMessage={emptyMessage}
        listAriaLabel={ariaLabel}
      />
    </div>,
    document.body
  );
}

const PRE_HINT = 'Recebe conversas abertas a partir de OS (apenas um time).';
const CONTRACTS_HINT = 'Recebe as conversas de contratos (apenas um time).';

function TeamFlagRow({
  label,
  hint,
  checked,
  onChange,
  disabled,
  disabledReason,
  saving,
}) {
  const locked = disabled || saving;
  return (
    <label
      className={`vch-team-flag-row${checked ? ' is-on' : ''}${
        disabled && !checked ? ' is-disabled' : ''
      }`}
    >
      <span className="vch-team-flag-copy">
        <span className="vch-team-flag-label">{label}</span>
        <span
          className={`vch-team-flag-hint${disabledReason ? ' vch-team-flag-hint--warn' : ''}`}
        >
          {disabledReason || hint}
        </span>
      </span>
      <span className={`pcs2-switch vch-hours-switch${locked ? ' pcs2-switch--disabled' : ''}`}>
        <input
          type="checkbox"
          checked={checked}
          disabled={locked}
          onChange={(e) => onChange(e.target.checked)}
          aria-label={label}
        />
        <span className="pcs2-switch-pill">
          <span className="pcs2-switch-dot" />
        </span>
      </span>
    </label>
  );
}

function teamFlagsState({
  teamId,
  preAtendimento,
  contracts,
  processEnabled,
  preAtendimentoTeamId,
  contractsTeamId,
}) {
  const isOther = (flagTeamId) =>
    flagTeamId != null && (teamId == null || Number(flagTeamId) !== Number(teamId));

  const preReason = !processEnabled
    ? 'Habilite o Pré atendimento em Customers → Processos.'
    : isOther(preAtendimentoTeamId)
      ? 'Já existe um time de pré-atendimento. Desmarque o atual antes.'
      : contracts
        ? 'Um time de contratos não pode ser de pré-atendimento.'
        : null;

  const contractsReason = isOther(contractsTeamId)
    ? 'Já existe um time de contratos. Desmarque o atual antes.'
    : preAtendimento
      ? 'Um time de pré-atendimento não pode ser de contratos.'
      : null;

  return {
    preDisabled: !preAtendimento && Boolean(preReason),
    preReason: preAtendimento ? null : preReason,
    contractsDisabled: !contracts && Boolean(contractsReason),
    contractsReason: contracts ? null : contractsReason,
  };
}

function TeamEditPopover({
  open,
  anchorRef,
  team,
  onClose,
  onSaved,
  processEnabled,
  preAtendimentoTeamId,
  contractsTeamId,
}) {
  const inputRef = useRef(null);
  const [description, setDescription] = useState('');
  const [preAtendimento, setPreAtendimento] = useState(false);
  const [contracts, setContracts] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !team) return;
    setDescription(String(team.description || team.name || '').trim());
    setPreAtendimento(Boolean(team.isPreAtendimento));
    setContracts(Boolean(team.isContracts));
    setSaving(false);
  }, [open, team]);

  const flags = teamFlagsState({
    teamId: team?.id,
    preAtendimento,
    contracts,
    processEnabled,
    preAtendimentoTeamId,
    contractsTeamId,
  });

  const handleConfirm = async () => {
    if (!team?.id) return;
    const text = description.trim();
    if (!text) {
      window.alert('Informe a descrição do time.');
      return;
    }
    setSaving(true);
    try {
      await updateChatTeam(team.id, {
        name: text,
        description: text,
        allowAutoAssign: false,
        preAtendimento: Boolean(preAtendimento),
        contracts: Boolean(contracts),
      });
      onClose?.();
      await onSaved?.();
    } catch (e) {
      window.alert(e.message || 'Erro ao atualizar time');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Ci2EditPopover
      open={open}
      anchorRef={anchorRef}
      onClose={onClose}
      width={294}
      maxHeight={440}
      estimatedHeight={360}
      title="Editar time"
      className="ci2-edit-popover--grupo ci2-edit-popover--single"
      initialFocusRef={inputRef}
    >
      <div className="ci2-edit-popover-head ci2-edit-popover-head--shadcn">
        <h4 className="ci2-edit-popover-title">Editar time</h4>
        <p className="ci2-edit-popover-sub">
          A descrição atualiza também o nome do time. Atribuição automática fica desmarcada.
        </p>
      </div>
      <div className="ci2-edit-popover-body ci2-grupo-rows">
        <div className="ci2-grupo-row">
          <label className="ci2-grupo-row-label" htmlFor="vch-team-edit-desc">
            Descrição
          </label>
          <div className="ci2-grupo-row-field">
            <input
              id="vch-team-edit-desc"
              ref={inputRef}
              type="text"
              className="ci2-pop-input ci2-grupo-row-input"
              placeholder="Descrição"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={saving}
              autoComplete="off"
            />
          </div>
        </div>
        <div className="vch-team-flags">
          <TeamFlagRow
            label="Pré-atendimento"
            hint={PRE_HINT}
            checked={preAtendimento}
            onChange={setPreAtendimento}
            disabled={flags.preDisabled}
            disabledReason={flags.preReason}
            saving={saving}
          />
          <TeamFlagRow
            label="Time de contratos"
            hint={CONTRACTS_HINT}
            checked={contracts}
            onChange={setContracts}
            disabled={flags.contractsDisabled}
            disabledReason={flags.contractsReason}
            saving={saving}
          />
        </div>
      </div>
      <Ci2EditPopoverActions
        onCancel={onClose}
        onConfirm={handleConfirm}
        confirmDisabled={!description.trim()}
        saving={saving}
      />
    </Ci2EditPopover>
  );
}

function TeamCreatePopover({
  open,
  anchorRef,
  onClose,
  onSaved,
  processEnabled,
  preAtendimentoTeamId,
  contractsTeamId,
}) {
  const inputRef = useRef(null);
  const [description, setDescription] = useState('');
  const [preAtendimento, setPreAtendimento] = useState(false);
  const [contracts, setContracts] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDescription('');
    setPreAtendimento(false);
    setContracts(false);
    setSaving(false);
  }, [open]);

  const flags = teamFlagsState({
    teamId: null,
    preAtendimento,
    contracts,
    processEnabled,
    preAtendimentoTeamId,
    contractsTeamId,
  });

  const handleConfirm = async () => {
    const text = description.trim();
    if (!text) {
      window.alert('Informe a descrição do time.');
      return;
    }
    setSaving(true);
    try {
      await createChatTeam({
        name: text,
        description: text,
        allowAutoAssign: false,
        preAtendimento: Boolean(preAtendimento) && !flags.preDisabled,
        contracts: Boolean(contracts) && !flags.contractsDisabled,
      });
      onClose?.();
      await onSaved?.();
    } catch (e) {
      window.alert(e.message || 'Erro ao criar time');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Ci2EditPopover
      open={open}
      anchorRef={anchorRef}
      onClose={onClose}
      width={294}
      maxHeight={440}
      estimatedHeight={360}
      title="Adicionar time"
      className="ci2-edit-popover--grupo ci2-edit-popover--single"
      initialFocusRef={inputRef}
    >
      <div className="ci2-edit-popover-head ci2-edit-popover-head--shadcn">
        <h4 className="ci2-edit-popover-title">Adicionar time</h4>
        <p className="ci2-edit-popover-sub">
          Informe a descrição. O nome do time será o mesmo e a atribuição automática fica
          desmarcada.
        </p>
      </div>
      <div className="ci2-edit-popover-body ci2-grupo-rows">
        <div className="ci2-grupo-row">
          <label className="ci2-grupo-row-label" htmlFor="vch-team-create-desc">
            Descrição
          </label>
          <div className="ci2-grupo-row-field">
            <input
              id="vch-team-create-desc"
              ref={inputRef}
              type="text"
              className="ci2-pop-input ci2-grupo-row-input"
              placeholder="Descrição"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={saving}
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleConfirm();
                }
              }}
            />
          </div>
        </div>
        <div className="vch-team-flags">
          <TeamFlagRow
            label="Pré-atendimento"
            hint={PRE_HINT}
            checked={preAtendimento}
            onChange={setPreAtendimento}
            disabled={flags.preDisabled}
            disabledReason={flags.preReason}
            saving={saving}
          />
          <TeamFlagRow
            label="Time de contratos"
            hint={CONTRACTS_HINT}
            checked={contracts}
            onChange={setContracts}
            disabled={flags.contractsDisabled}
            disabledReason={flags.contractsReason}
            saving={saving}
          />
        </div>
      </div>
      <Ci2EditPopoverActions
        onCancel={onClose}
        onConfirm={handleConfirm}
        confirmDisabled={!description.trim()}
        saving={saving}
      />
    </Ci2EditPopover>
  );
}

function matchesSearch(text, query) {
  if (!query) return true;
  return String(text || '')
    .toLowerCase()
    .includes(query);
}

/**
 * Visão agrupada: times × agentes + usuários ativos elegíveis.
 */
export function CustomersChatAgentsTeamsPanel({ enabled }) {
  const [groups, setGroups] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [agentsIndex, setAgentsIndex] = useState(() => new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState({ [ATIVOS_KEY]: true });
  const [busyKey, setBusyKey] = useState(null);
  const [rolePicker, setRolePicker] = useState(null);
  const [editTeam, setEditTeam] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [teamPicker, setTeamPicker] = useState(null);
  const [chatOnlyByUserId, setChatOnlyByUserId] = useState(() => new Map());
  const [chatOnlyHelpOpen, setChatOnlyHelpOpen] = useState(false);
  const [chatOnlyBusyId, setChatOnlyBusyId] = useState(null);
  const [disableConfirm, setDisableConfirm] = useState(null);
  const [deleteTeamConfirm, setDeleteTeamConfirm] = useState(null);
  const [preAtendimentoTeamId, setPreAtendimentoTeamId] = useState(null);
  const [contractsTeamId, setContractsTeamId] = useState(null);
  const [preProcessEnabled, setPreProcessEnabled] = useState(false);
  const [codtechEdit, setCodtechEdit] = useState(null);
  const [goTechCatalog, setGoTechCatalog] = useState([]);
  const [codtechBusyId, setCodtechBusyId] = useState(null);
  const roleAnchorRef = useRef(null);
  const codtechAnchorRef = useRef(null);
  const editAnchorRef = useRef(null);
  const createAnchorRef = useRef(null);
  const teamPickerAnchorRef = useRef(null);

  const load = useCallback(async (opts) => {
    if (!enabled) return null;
    const silent = Boolean(opts && typeof opts === 'object' && opts.silent);
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [teamsRes, agentsRes, eligibleRes, chatOnlyMap, preRes] = await Promise.all([
        fetchChatTeams(),
        fetchChatAgents(),
        fetchChatEligibleUsers(),
        fetchUsersChatOnlyMap().catch(() => new Map()),
        fetchChatPreAtendimento().catch(() => ({ data: { teamId: null, processEnabled: false } })),
      ]);
      const teams = Array.isArray(teamsRes?.data) ? teamsRes.data : [];
      const allAgents = Array.isArray(agentsRes?.data) ? agentsRes.data : [];
      const eligibleList = Array.isArray(eligibleRes?.data) ? eligibleRes.data : [];
      setChatOnlyByUserId(chatOnlyMap);
      setPreAtendimentoTeamId(
        preRes?.data?.teamId != null && Number(preRes.data.teamId) > 0
          ? Number(preRes.data.teamId)
          : null
      );
      setPreProcessEnabled(Boolean(preRes?.data?.processEnabled));
      const contractsTeam = teams.find((t) => t?.isContracts);
      setContractsTeamId(contractsTeam ? Number(contractsTeam.id) : null);

      const byId = new Map();
      allAgents.forEach((a) => {
        const id = agentId(a);
        if (id != null) byId.set(id, a);
      });
      setAgentsIndex(byId);

      const loaded = await Promise.all(
        teams.map(async (team, index) => {
          let agents = [];
          try {
            const agentsResTeam = await fetchChatTeamAgents(team.id);
            agents = Array.isArray(agentsResTeam?.data) ? agentsResTeam.data : [];
          } catch {
            agents = [];
          }
          agents = agents.map((a) => {
            const id = agentId(a);
            const linked = id != null ? byId.get(id) : null;
            return linked ? { ...a, ...linked } : a;
          });
          return {
            key: String(team.id),
            team,
            agents,
            color: TEAM_DOT_COLORS[index % TEAM_DOT_COLORS.length],
            isUnassigned: false,
          };
        })
      );

      const assignedIds = new Set();
      loaded.forEach((g) => {
        (g.agents || []).forEach((a) => {
          const id = agentId(a);
          if (id != null) assignedIds.add(id);
        });
      });
      const unassigned = allAgents.filter((a) => {
        const id = agentId(a);
        return id != null && !assignedIds.has(id);
      });
      if (unassigned.length > 0) {
        loaded.push({
          key: SEM_TIME_KEY,
          team: { id: null, name: 'Sem time' },
          agents: unassigned,
          color: SEM_TIME_COLOR,
          isUnassigned: true,
        });
      }

      setGroups(loaded);
      setEligible(eligibleList);
      setCollapsed((prev) => {
        const next = { ...prev };
        loaded.forEach((g) => {
          const empty = (g.agents?.length || 0) === 0;
          if (next[g.key] === undefined) next[g.key] = empty;
        });
        if (next[ATIVOS_KEY] === undefined) next[ATIVOS_KEY] = true;
        return next;
      });
      return { groups: loaded, eligible: eligibleList, agents: allAgents };
    } catch (e) {
      setError(e.message || 'Erro ao carregar times e agentes');
      setGroups([]);
      setEligible([]);
      setAgentsIndex(new Map());
      setChatOnlyByUserId(new Map());
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load]);

  const totalAgents = useMemo(
    () => groups.reduce((sum, g) => sum + (g.agents?.length || 0), 0),
    [groups]
  );

  const search = String(searchQuery || '').trim().toLowerCase();

  const filteredGroups = useMemo(() => {
    if (!search) return groups;
    return groups
      .map((group) => {
        const teamName = String(group.team?.name || '').trim();
        const teamDesc = String(group.team?.description || '').trim();
        const teamMatch =
          matchesSearch(teamName, search) || matchesSearch(teamDesc, search);
        const agents = (group.agents || []).filter(
          (agent) =>
            teamMatch ||
            matchesSearch(agentName(agent), search) ||
            matchesSearch(agentEmail(agent), search) ||
            matchesSearch(agentRole(agent), search)
        );
        if (!teamMatch && agents.length === 0) return null;
        return { ...group, agents: teamMatch ? group.agents : agents };
      })
      .filter(Boolean);
  }, [groups, search]);

  const filteredEligible = useMemo(() => {
    if (!search) return eligible;
    return eligible.filter((user) => {
      const name = user.displayName || user.email || user.name || '';
      const email = user.loginEmail || user.name || user.email || '';
      return matchesSearch(name, search) || matchesSearch(email, search);
    });
  }, [eligible, search]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const token = localStorage.getItem('authToken');
    fetch('/api/config/go/users/link-catalog', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => res.json())
      .then((catalogRes) => {
        if (cancelled) return;
        if (catalogRes?.success) {
          const payload = catalogRes.data;
          const tech = Array.isArray(payload?.tech)
            ? payload.tech
            : Array.isArray(payload)
              ? payload
              : [];
          setGoTechCatalog(tech);
        }
      })
      .catch(() => {
        if (!cancelled) setGoTechCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // Só bloqueia código já usado por outro agente do chat (não por App Go / painel).
  const usedTechMap = useMemo(() => {
    const map = {};
    for (const g of groups) {
      for (const a of g.agents || []) {
        const key = goCodeKey(a.codtech);
        const id = agentId(a);
        if (key && id != null) {
          map[key] = { idUser: id, username: agentName(a) };
        }
      }
    }
    return map;
  }, [groups]);

  const goTechNameByCode = useMemo(() => {
    const map = new Map();
    for (const item of goTechCatalog) {
      const key = goCodeKey(item?.code);
      if (!key) continue;
      const name = String(item?.name || '').trim();
      if (name) map.set(key, name);
    }
    return map;
  }, [goTechCatalog]);

  const resolveTechName = useCallback(
    (code) => {
      const key = goCodeKey(code);
      return key ? goTechNameByCode.get(key) || '' : '';
    },
    [goTechNameByCode]
  );

  const handleSaveAgentCodtech = useCallback(
    async (agent, value) => {
      const id = agentId(agent);
      if (id == null) return;
      setCodtechBusyId(id);
      setError(null);
      try {
        await updateChatAgentCodtech(id, value || null);
        setCodtechEdit(null);
        await load({ silent: true });
      } catch (e) {
        setError(e.message || 'Falha ao vincular código do técnico');
      } finally {
        setCodtechBusyId(null);
      }
    },
    [load]
  );

  const photoSubjects = useMemo(() => {
    const list = [];
    for (const g of groups) {
      for (const a of g.agents || []) {
        const id = Number(a.verveUserId);
        if (Number.isFinite(id) && id > 0) {
          list.push({
            id,
            hasProfilePhoto: a.hasProfilePhoto === true,
            profilePhotoUrl: a.profilePhotoUrl || null,
          });
        }
      }
    }
    for (const u of eligible) {
      const id = Number(u.id);
      if (Number.isFinite(id) && id > 0) {
        list.push({
          id,
          hasProfilePhoto: u.hasProfilePhoto === true,
          profilePhotoUrl: u.profilePhotoUrl || null,
        });
      }
    }
    return list;
  }, [groups, eligible]);

  const photoMap = useUserProfilePhotos(photoSubjects);

  const toggleGroup = useCallback((key) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const groupKeys = useMemo(
    () => [...filteredGroups.map((g) => g.key), ATIVOS_KEY],
    [filteredGroups]
  );

  const allCollapsed = useMemo(
    () => groupKeys.length > 0 && groupKeys.every((k) => !!collapsed[k]),
    [groupKeys, collapsed]
  );

  const toggleAllGroups = useCallback(() => {
    const nextAllCollapsed = !allCollapsed;
    const next = {};
    groupKeys.forEach((k) => {
      next[k] = nextAllCollapsed;
    });
    setCollapsed(next);
  }, [allCollapsed, groupKeys]);

  const handleDisableAgent = useCallback((agent) => {
    const id = agentId(agent);
    const linked = id != null ? agentsIndex.get(id) || agent : agent;
    const hasCwId = id != null;
    const hasFallback = Number(linked.linkId) > 0 || Number(linked.verveUserId) > 0;
    if (!hasCwId && !hasFallback) {
      window.alert('Não foi possível identificar o vínculo deste agente.');
      return;
    }
    setDisableConfirm({ agent: linked });
  }, [agentsIndex]);

  const closeDisableConfirm = useCallback(() => {
    if (busyKey) return;
    setDisableConfirm(null);
  }, [busyKey]);

  const applyDisableOptimistic = useCallback((agent) => {
    const id = agentId(agent);
    const verveId = agentVerveUserId(agent);
    setGroups((prev) =>
      prev
        .map((g) => ({
          ...g,
          agents: (g.agents || []).filter((a) => !sameAgentRef(a, agent)),
        }))
        .filter((g) => !g.isUnassigned || (g.agents?.length || 0) > 0)
    );
    if (id != null) {
      setAgentsIndex((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    }
    if (verveId != null) {
      setEligible((prev) => {
        if (prev.some((u) => Number(u.id) === verveId)) return prev;
        const email = String(agent.email || agent.loginEmail || '').trim();
        const display =
          String(agent.name || agent.available_name || agent.displayName || '').trim() ||
          email ||
          `Usuário #${verveId}`;
        return [
          {
            id: verveId,
            name: email || display,
            email: display,
            displayName: display,
            loginEmail: email || display,
            hasProfilePhoto: agent.hasProfilePhoto === true,
            profilePhotoUrl: agent.profilePhotoUrl || null,
          },
          ...prev,
        ];
      });
    }
  }, []);

  const revealAfterDisable = useCallback((agent, snapshot) => {
    const verveId = agentVerveUserId(agent);
    const groupsSnap = snapshot?.groups || [];
    const eligibleSnap = snapshot?.eligible || [];
    const stillUnassigned = groupsSnap.some(
      (g) =>
        g.isUnassigned &&
        (g.agents || []).some(
          (a) => sameAgentRef(a, agent) || (verveId != null && agentVerveUserId(a) === verveId)
        )
    );
    const inEligible =
      verveId != null && eligibleSnap.some((u) => Number(u.id) === verveId);
    setCollapsed((prev) => ({
      ...prev,
      ...(stillUnassigned ? { [SEM_TIME_KEY]: false } : {}),
      ...(inEligible || !stillUnassigned ? { [ATIVOS_KEY]: false } : {}),
    }));
  }, []);

  const confirmDisableAgent = useCallback(async () => {
    const agent = disableConfirm?.agent;
    if (!agent) return;
    const id = agentId(agent);
    const hasCwId = id != null;
    const busy = hasCwId ? `agent-${id}` : `link-${agent.linkId || agent.verveUserId}`;
    setBusyKey(busy);
    try {
      await deleteChatAgent(hasCwId ? id : 0, {
        linkId: agent.linkId,
        verveUserId: agent.verveUserId,
      });
      applyDisableOptimistic(agent);
      setDisableConfirm(null);
      setCollapsed((prev) => ({ ...prev, [ATIVOS_KEY]: false }));
      const snapshot = await load({ silent: true });
      revealAfterDisable(agent, snapshot);
    } catch (e) {
      window.alert(e.message || 'Erro ao remover agente');
    } finally {
      setBusyKey(null);
    }
  }, [disableConfirm, applyDisableOptimistic, load, revealAfterDisable]);

  const handleSetChatOnly = useCallback(async (verveUserId, nextOnly) => {
    const id = Number(verveUserId);
    if (!Number.isFinite(id) || id <= 0 || chatOnlyBusyId != null) return;
    setChatOnlyBusyId(id);
    const prev = chatOnlyByUserId.get(id) === true;
    setChatOnlyByUserId((map) => {
      const next = new Map(map);
      next.set(id, nextOnly);
      return next;
    });
    try {
      await setUserChatOnly(id, nextOnly);
    } catch (e) {
      setChatOnlyByUserId((map) => {
        const next = new Map(map);
        next.set(id, prev);
        return next;
      });
      window.alert(e.message || 'Erro ao atualizar somente chat');
    } finally {
      setChatOnlyBusyId(null);
    }
  }, [chatOnlyBusyId, chatOnlyByUserId]);

  const handleDeleteTeam = useCallback((team) => {
    if (!team?.id) return;
    setDeleteTeamConfirm({ team });
  }, []);

  const closeDeleteTeamConfirm = useCallback(() => {
    if (busyKey) return;
    setDeleteTeamConfirm(null);
  }, [busyKey]);

  const confirmDeleteTeam = useCallback(async () => {
    const team = deleteTeamConfirm?.team;
    if (!team?.id) return;
    setBusyKey(`team-${team.id}`);
    try {
      await deleteChatTeam(team.id);
      setDeleteTeamConfirm(null);
      await load({ silent: true });
    } catch (e) {
      window.alert(e.message || 'Erro ao remover');
    } finally {
      setBusyKey(null);
    }
  }, [deleteTeamConfirm, load]);

  const openTeamEdit = useCallback((team, el) => {
    setCreateOpen(false);
    editAnchorRef.current = el;
    setEditTeam(team);
  }, []);

  const closeTeamEdit = useCallback(() => {
    setEditTeam(null);
  }, []);

  const openTeamCreate = useCallback(() => {
    setEditTeam(null);
    setCreateOpen((open) => !open);
  }, []);

  const closeTeamCreate = useCallback(() => {
    setCreateOpen(false);
  }, []);

  const memberIdsOfTeam = useCallback(
    (teamId) => {
      const tid = Number(teamId);
      if (!Number.isFinite(tid) || tid <= 0) return [];
      const group = groups.find(
        (g) => !g.isUnassigned && Number(g.team?.id) === tid
      );
      if (!group) return [];
      return (group.agents || [])
        .map((a) => agentId(a))
        .filter((id) => id != null);
    },
    [groups]
  );

  const teamsForPicker = useCallback(
    (agent, fromTeamId, mode) => {
      const aid = agentId(agent);
      const fromId = Number(fromTeamId);
      return groups
        .filter((g) => {
          if (g.isUnassigned) return false;
          const tid = Number(g.team?.id);
          if (!Number.isFinite(tid) || tid <= 0) return false;
          if (Number.isFinite(fromId) && fromId > 0 && tid === fromId) return false;
          if (mode === 'copy' && aid != null) {
            const already = (g.agents || []).some((a) => agentId(a) === aid);
            if (already) return false;
          }
          return true;
        })
        .map((g) => ({
          id: Number(g.team.id),
          label: String(g.team?.name || '').trim() || `Time #${g.team.id}`,
        }));
    },
    [groups]
  );

  const openTeamPicker = useCallback((mode, agent, fromTeamId, el) => {
    teamPickerAnchorRef.current = el;
    const rect = el?.getBoundingClientRect?.() || null;
    setTeamPicker({ mode, agent, fromTeamId, rect });
  }, []);

  const closeTeamPicker = useCallback(() => {
    setTeamPicker(null);
  }, []);

  const handleTeamPickerSelect = useCallback(
    async (targetTeam) => {
      const picker = teamPicker;
      if (!picker || !targetTeam?.id) return;
      const aid = agentId(picker.agent);
      if (aid == null) {
        window.alert('Não foi possível identificar o agente.');
        return;
      }
      const fromId = Number(picker.fromTeamId);
      const hasFromTeam = Number.isFinite(fromId) && fromId > 0;
      const toId = Number(targetTeam.id);
      setTeamPicker(null);
      setBusyKey(`team-agent-${aid}`);
      try {
        const toIds = [...new Set([...memberIdsOfTeam(toId), aid])];
        if (picker.mode === 'move' && hasFromTeam) {
          const fromIds = memberIdsOfTeam(fromId).filter((id) => id !== aid);
          await assignChatTeamAgents(fromId, fromIds);
        }
        await assignChatTeamAgents(toId, toIds);
        await load();
      } catch (e) {
        const defining = picker.mode === 'move' && !hasFromTeam;
        window.alert(
          e.message ||
            (picker.mode === 'move'
              ? defining
                ? 'Erro ao definir time do agente'
                : 'Erro ao alterar time do agente'
              : 'Erro ao replicar agente para o time')
        );
      } finally {
        setBusyKey(null);
      }
    },
    [teamPicker, memberIdsOfTeam, load]
  );

  useLayoutEffect(() => {
    if (!teamPicker) return undefined;
    const onResize = () => {
      const el = teamPickerAnchorRef.current;
      if (!el) return;
      setTeamPicker((prev) =>
        prev ? { ...prev, rect: el.getBoundingClientRect() } : null
      );
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [teamPicker]);

  const openRolePicker = useCallback((user, el) => {
    roleAnchorRef.current = el;
    const rect = el?.getBoundingClientRect?.() || null;
    setRolePicker({ user, rect });
  }, []);

  const handleEnableUser = useCallback(
    async (role) => {
      const user = rolePicker?.user;
      if (!user?.id || !role) return;
      setRolePicker(null);
      setBusyKey(`user-${user.id}`);
      try {
        await createChatAgent({
          verveUserId: Number(user.id),
          role,
        });
        await load();
      } catch (e) {
        window.alert(e.message || 'Erro ao vincular agente');
      } finally {
        setBusyKey(null);
      }
    },
    [rolePicker, load]
  );

  useLayoutEffect(() => {
    if (!rolePicker) return undefined;
    const onResize = () => {
      const el = roleAnchorRef.current;
      if (!el) return;
      setRolePicker((prev) =>
        prev ? { ...prev, rect: el.getBoundingClientRect() } : null
      );
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [rolePicker]);

  const ativosCollapsed = !!collapsed[ATIVOS_KEY] && !search;
  const showTable =
    !loading && (filteredGroups.length > 0 || filteredEligible.length > 0 || groups.length > 0);

  return (
    <section className="pcs2-section pcs2-section--stacked vch-section vch-section--full">
      <div className="pcs2-section-top">
        <div className="pcs2-section-heading">
          <Network size={16} strokeWidth={1.75} aria-hidden />
          <h3 className="pcs2-section-title">Agentes e times</h3>
        </div>
        <p className="pcs2-section-desc">
          Visão dos times e dos agentes vinculados a cada fila de atendimento.
        </p>
      </div>
      <div className="pcs2-section-body pcs2-section-body--full">
        {error ? <p className="vch-error">{error}</p> : null}

        <div className="pcs2-subblock vch-agents-teams-subblock">
          <div className="pcs2-subhead pcs2-subhead--with-action">
            <div className="pcs2-subhead-copy" />
            <div className="pcs2-subhead-action">
              <div className="vch-agents-teams-toolbar">
                <BoardToolbarExpandSearch
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Pesquisar agentes e times..."
                  ariaLabel="Pesquisar agentes e times"
                />
                <EcClickUpFilterChip
                  anchorRef={createAnchorRef}
                  label="Adicionar time"
                  icon={IconPlus}
                  hasSelection={createOpen}
                  isOpen={createOpen}
                  showValue={false}
                  ariaLabel="Adicionar time"
                  onClick={openTeamCreate}
                />
              </div>
            </div>
          </div>

          <div className="ci2-root vch-ci2 vch-agents-teams-ci2">
            <div className="ci2-panel">
              {loading ? (
                <div className="ci2-foot-note">Carregando…</div>
              ) : !showTable ? (
                <div className="ci2-foot-note">Nenhum time ou usuário disponível.</div>
              ) : search && filteredGroups.length === 0 && filteredEligible.length === 0 ? (
                <div className="ci2-foot-note">Nenhum resultado para a pesquisa.</div>
              ) : (
                <>
                  <div
                    className={`ci2-table-scroll vch-ci2-scroll vch-agents-teams-scroll${
                      !ativosCollapsed ? ' vch-agents-teams-scroll--expanded' : ''
                    }`}
                  >
                    <table className="ci2-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Nome</th>
                          <th>Cód. técnico</th>
                          <th>E-mail</th>
                          <th>Papel</th>
                          <th className={chatOnlyHelpOpen ? 'ci2-th-has-help-open' : ''}>
                            <span className="ci2-th-inner ci2-th-has-help">
                              Somente Chat
                              <Ci2HelpHexIcon
                                label="Ajuda sobre Somente Chat"
                                expanded={chatOnlyHelpOpen}
                                onToggle={() => setChatOnlyHelpOpen((v) => !v)}
                              />
                              {chatOnlyHelpOpen ? (
                                <div
                                  className="ci2-th-help-popover ci2-th-help-popover--align-end"
                                  role="tooltip"
                                >
                                  {CHAT_ONLY_HELP}
                                </div>
                              ) : null}
                            </span>
                          </th>
                          <th aria-label="Alterar time" />
                          <th aria-label="Replicar time" />
                        </tr>
                      </thead>
                      <tbody>
                        {filteredGroups.map((group) => {
                          const isCollapsed = search ? false : !!collapsed[group.key];
                          const isUnassigned = !!group.isUnassigned;
                          const teamName = isUnassigned
                            ? 'Sem time'
                            : String(group.team?.name || '').trim() ||
                              `Time #${group.team?.id}`;
                          const teamDesc = isUnassigned
                            ? ''
                            : String(group.team?.description || '').trim();
                          const count = group.agents.length;

                          return (
                            <React.Fragment key={group.key}>
                              <tr
                                className={`ci2-group-row${isCollapsed ? ' collapsed' : ''}`}
                                onClick={() => toggleGroup(group.key)}
                              >
                                <td colSpan={TABLE_COLSPAN}>
                                  <div className="vch-agents-teams-group-row">
                                    <div className="ci2-group-label">
                                      <span className="ci2-chev">
                                        <ChevronDown size={13} strokeWidth={2.5} />
                                      </span>
                                      <span
                                        className="ci2-dot"
                                        style={{ background: group.color }}
                                        aria-hidden
                                      />
                                      {teamName}
                                      <span className="ci2-group-count">· {count}</span>
                                      {!isUnassigned && group.team?.isPreAtendimento ? (
                                        <span className="vch-team-pre-badge">Pré-atendimento</span>
                                      ) : null}
                                      {!isUnassigned && group.team?.isContracts ? (
                                        <span className="vch-team-pre-badge vch-team-contracts-badge">
                                          Contratos
                                        </span>
                                      ) : null}
                                      {teamDesc ? (
                                        <span className="ci2-group-desc">{teamDesc}</span>
                                      ) : null}
                                    </div>
                                    {!isUnassigned ? (
                                      <div className="vch-agents-teams-group-actions">
                                        <button
                                          type="button"
                                          className={`vch-agents-teams-group-edit ci2-modern-tooltip-host${
                                            editTeam?.id === group.team?.id
                                              ? ' vch-agents-teams-group-edit--open'
                                              : ''
                                          }`}
                                          data-tooltip="Editar time"
                                          aria-label={`Editar ${teamName}`}
                                          aria-expanded={editTeam?.id === group.team?.id}
                                          disabled={!!busyKey}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openTeamEdit(group.team, e.currentTarget);
                                          }}
                                        >
                                          <Pencil size={14} strokeWidth={2} />
                                        </button>
                                        <button
                                          type="button"
                                          className="vch-agents-teams-group-delete ci2-modern-tooltip-host"
                                          data-tooltip="Remover time"
                                          aria-label={`Remover ${teamName}`}
                                          disabled={!!busyKey}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteTeam(group.team);
                                          }}
                                        >
                                          {busyKey === `team-${group.team?.id}` ? (
                                            <Loader2 size={14} className="vch-spin" />
                                          ) : (
                                            <Trash2 size={14} />
                                          )}
                                        </button>
                                      </div>
                                    ) : null}
                                  </div>
                                </td>
                              </tr>
                              {!isCollapsed
                                ? group.agents.map((agent, idx) => {
                                    const id = agentId(agent);
                                    const busy =
                                      busyKey ===
                                        (id != null
                                          ? `agent-${id}`
                                          : `link-${agent.linkId || agent.verveUserId}`) ||
                                      busyKey === `team-agent-${id}`;
                                    const teamBusy = !!busyKey;
                                    const defineTeamLabel = isUnassigned
                                      ? 'Definir time'
                                      : 'Alterar time';
                                    const agentCodtech = String(agent.codtech || '').trim();
                                    const agentCodtechName = agentCodtech
                                      ? resolveTechName(agentCodtech)
                                      : '';
                                    const agentCodtechTip = agentCodtech
                                      ? agentCodtechName
                                        ? `${agentCodtech} · ${agentCodtechName}`
                                        : agentCodtech
                                      : 'Vincular código do técnico';
                                    return (
                                      <tr
                                        key={
                                          id != null
                                            ? `${group.key}-${id}`
                                            : `${group.key}-${idx}`
                                        }
                                        className="ci2-data-row"
                                        style={{ '--vch-team-accent': group.color }}
                                      >
                                        <td className="ci2-td ci2-td-center ci2-td-permite">
                                          <button
                                            type="button"
                                            className="ci2-lock-toggle on ci2-modern-tooltip-host"
                                            data-tooltip="Desabilitar agente"
                                            aria-label={`Desabilitar ${agentName(agent)}`}
                                            aria-pressed="true"
                                            disabled={teamBusy}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDisableAgent(agent);
                                            }}
                                          >
                                            <span className="ci2-knob">
                                              {busyKey ===
                                                (id != null
                                                  ? `agent-${id}`
                                                  : `link-${agent.linkId || agent.verveUserId}`) ? null : (
                                                <Check size={7} strokeWidth={3} aria-hidden />
                                              )}
                                            </span>
                                          </button>
                                        </td>
                                        <td className="ci2-td" title={agentName(agent)}>
                                          <span className="vch-agents-teams-name">
                                            <AgentAvatar person={agent} photoMap={photoMap} />
                                            <span className="vch-agents-teams-name-text">
                                              {agentName(agent)}
                                            </span>
                                          </span>
                                        </td>
                                        <td className="ci2-td">
                                          {!isUnassigned && group.team?.isPreAtendimento ? (
                                            <span
                                              className="vch-agent-codtech-tip ci2-modern-tooltip-host"
                                              data-tooltip={agentCodtechTip}
                                            >
                                              <button
                                                type="button"
                                                className="vch-agent-codtech-btn"
                                                aria-label={agentCodtechTip}
                                                disabled={codtechBusyId != null || teamBusy}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  codtechAnchorRef.current = e.currentTarget;
                                                  setCodtechEdit({
                                                    agent,
                                                    value: agentCodtech,
                                                  });
                                                }}
                                              >
                                                {agentCodtech || 'Vincular'}
                                              </button>
                                            </span>
                                          ) : (
                                            <span className="vch-agent-codtech-empty">—</span>
                                          )}
                                        </td>
                                        <td
                                          className="ci2-td vch-mono"
                                          title={agentEmail(agent) || undefined}
                                        >
                                          {agentEmail(agent)}
                                        </td>
                                        <td className="ci2-td" title={agentRole(agent) || undefined}>
                                          {agentRole(agent)}
                                        </td>
                                        <td className="ci2-td ci2-td-center vch-agents-teams-chat-only-col">
                                          {!isUnassigned
                                            ? (() => {
                                                const verveId = agentVerveUserId(agent);
                                                const checked =
                                                  verveId != null &&
                                                  chatOnlyByUserId.get(verveId) === true;
                                                return (
                                                  <Ci2Switch
                                                    checked={checked}
                                                    disabled={
                                                      verveId == null ||
                                                      chatOnlyBusyId != null ||
                                                      teamBusy
                                                    }
                                                    onChange={(next) =>
                                                      handleSetChatOnly(verveId, next)
                                                    }
                                                    tooltip={
                                                      checked
                                                        ? 'Desabilitar somente chat'
                                                        : 'Habilitar somente chat'
                                                    }
                                                  />
                                                );
                                              })()
                                            : null}
                                        </td>
                                        <td className="ci2-td ci2-td-center vch-agents-teams-action-col">
                                          <button
                                            type="button"
                                            className={`vch-agents-teams-row-action ci2-modern-tooltip-host${
                                              isUnassigned
                                                ? ' vch-agents-teams-row-action--define'
                                                : ''
                                            }`}
                                            data-tooltip={defineTeamLabel}
                                            aria-label={`${defineTeamLabel} de ${agentName(agent)}`}
                                            disabled={teamBusy || id == null}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openTeamPicker(
                                                'move',
                                                agent,
                                                isUnassigned ? null : group.team.id,
                                                e.currentTarget
                                              );
                                            }}
                                          >
                                            {busyKey === `team-agent-${id}` ? (
                                              <Loader2 size={14} className="vch-spin" />
                                            ) : (
                                              <IconTransferVertical size={15} stroke={1.5} />
                                            )}
                                          </button>
                                        </td>
                                        <td className="ci2-td ci2-td-center vch-agents-teams-action-col">
                                          {!isUnassigned ? (
                                            <button
                                              type="button"
                                              className="vch-agents-teams-row-action ci2-modern-tooltip-host"
                                              data-tooltip="Replicar para outro time"
                                              aria-label={`Replicar ${agentName(agent)} para outro time`}
                                              disabled={teamBusy || id == null}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openTeamPicker(
                                                  'copy',
                                                  agent,
                                                  group.team.id,
                                                  e.currentTarget
                                                );
                                              }}
                                            >
                                              <IconCopyCheck size={15} stroke={1.5} />
                                            </button>
                                          ) : null}
                                        </td>
                                      </tr>
                                    );
                                  })
                                : null}
                              {!isCollapsed && count === 0 ? (
                                <tr className="ci2-data-row">
                                  <td className="ci2-td vch-empty" colSpan={TABLE_COLSPAN}>
                                    {isUnassigned
                                      ? 'Nenhum agente sem time.'
                                      : 'Nenhum agente neste time.'}
                                  </td>
                                </tr>
                              ) : null}
                            </React.Fragment>
                          );
                        })}

                        <tr
                          className={`ci2-group-row${ativosCollapsed ? ' collapsed' : ''}`}
                          onClick={() => toggleGroup(ATIVOS_KEY)}
                        >
                          <td colSpan={TABLE_COLSPAN}>
                            <div className="ci2-group-label">
                              <span className="ci2-chev">
                                <ChevronDown size={13} strokeWidth={2.5} />
                              </span>
                              <span
                                className="ci2-dot"
                                style={{
                                  border: '1.5px dashed #767889',
                                  background: 'transparent',
                                }}
                                aria-hidden
                              />
                              Usuários disponíveis
                              <span className="ci2-group-count">· {filteredEligible.length}</span>
                              <span className="ci2-group-desc">Não vinculados como agentes</span>
                            </div>
                          </td>
                        </tr>
                        {!ativosCollapsed
                          ? filteredEligible.map((user) => {
                              const busy = busyKey === `user-${user.id}`;
                              const name =
                                user.displayName || user.email || user.name || '—';
                              const verveId = Number(user.id);
                              const chatOnly =
                                Number.isFinite(verveId) &&
                                chatOnlyByUserId.get(verveId) === true;
                              return (
                                <tr
                                  key={`ativo-${user.id}`}
                                  className="ci2-data-row cat-desabilitada"
                                >
                                  <td className="ci2-td ci2-td-center ci2-td-permite">
                                    {busy ? (
                                      <span className="ci2-enabling">
                                        <span className="ci2-enabling-spinner" aria-hidden />
                                      </span>
                                    ) : (
                                      <button
                                        type="button"
                                        className="ci2-enable-affordance ci2-modern-tooltip-host"
                                        data-tooltip="Clique para vincular como agente"
                                        aria-label={`Vincular ${name}`}
                                        disabled={!!busyKey}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openRolePicker(user, e.currentTarget);
                                        }}
                                      >
                                        <svg
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="3"
                                          strokeLinecap="round"
                                          aria-hidden
                                        >
                                          <path d="M12 5v14M5 12h14" />
                                        </svg>
                                      </button>
                                    )}
                                  </td>
                                  <td className="ci2-td" title={name}>
                                    <span className="vch-agents-teams-name">
                                      <AgentAvatar person={user} photoMap={photoMap} />
                                      <span className="vch-agents-teams-name-text">{name}</span>
                                    </span>
                                  </td>
                                  <td className="ci2-td">
                                    <span className="vch-agent-codtech-empty">—</span>
                                  </td>
                                  <td
                                    className="ci2-td vch-mono"
                                    title={
                                      user.loginEmail || user.name || user.email || undefined
                                    }
                                  >
                                    {user.loginEmail || user.name || user.email || '—'}
                                  </td>
                                  <td className="ci2-td">—</td>
                                  <td className="ci2-td ci2-td-center vch-agents-teams-chat-only-col">
                                    <Ci2Switch
                                      checked={chatOnly}
                                      disabled={
                                        !Number.isFinite(verveId) ||
                                        verveId <= 0 ||
                                        chatOnlyBusyId != null ||
                                        !!busyKey
                                      }
                                      onChange={(next) => handleSetChatOnly(verveId, next)}
                                      tooltip={
                                        chatOnly
                                          ? 'Desabilitar somente chat'
                                          : 'Habilitar somente chat'
                                      }
                                    />
                                  </td>
                                  <td className="ci2-td" />
                                  <td className="ci2-td" />
                                </tr>
                              );
                            })
                          : null}
                        {!ativosCollapsed && filteredEligible.length === 0 ? (
                          <tr className="ci2-data-row cat-desabilitada">
                            <td className="ci2-td vch-empty" colSpan={TABLE_COLSPAN}>
                              Nenhum usuário disponível para vínculo.
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                  <div className="ci2-foot-note">
                    <span>
                      {totalAgents} agente{totalAgents !== 1 ? 's' : ''} · {groups.length} time
                      {groups.length !== 1 ? 's' : ''} · {eligible.length}{' '}
                      {eligible.length !== 1 ? 'disponíveis' : 'disponível'}
                    </span>
                    <button
                      type="button"
                      className="board-clickup-filter-chip ci2-foot-toggle"
                      onClick={toggleAllGroups}
                      aria-label={
                        allCollapsed ? 'Expandir todos os grupos' : 'Recolher todos os grupos'
                      }
                    >
                      <span className="board-clickup-filter-chip-text">
                        <span className="board-clickup-filter-chip-label">
                          {allCollapsed ? 'Expandir grupos' : 'Recolher grupos'}
                        </span>
                      </span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {codtechEdit ? (
        <Ci2EditPopover
          open
          anchorRef={codtechAnchorRef}
          onClose={() => setCodtechEdit(null)}
          width={360}
          maxHeight={420}
          estimatedHeight={220}
          title="Código do técnico"
          className="ci2-edit-popover--grupo ci2-edit-popover--single vch-agent-codtech-popover"
        >
          <div className="ci2-edit-popover-head ci2-edit-popover-head--shadcn">
            <h4 className="ci2-edit-popover-title">Código do técnico</h4>
            <p className="ci2-edit-popover-sub">
              Vincule o código ERP (4 dígitos) do agente em pré-atendimento.
            </p>
          </div>
          <div className="ci2-edit-popover-body ci2-grupo-row">
            <GoUsrCodePicker
              field="codtech"
              value={codtechEdit.value || ''}
              onChange={(v) => setCodtechEdit((prev) => (prev ? { ...prev, value: v } : prev))}
              catalog={{ tech: goTechCatalog, moto: [] }}
              usedMap={usedTechMap}
              excludeIdUser={agentId(codtechEdit.agent)}
              inputId="vch-agent-codtech"
              listOnSearchOnly
              includeOccupied
            />
          </div>
          <Ci2EditPopoverActions
            onCancel={() => setCodtechEdit(null)}
            onConfirm={() => handleSaveAgentCodtech(codtechEdit.agent, codtechEdit.value)}
            confirmDisabled={
              Boolean(codtechEdit.value) &&
              (!isValidGoCode(codtechEdit.value) ||
                isGoCodeTaken(
                  usedTechMap,
                  codtechEdit.value,
                  agentId(codtechEdit.agent)
                ))
            }
            saving={codtechBusyId != null}
          />
        </Ci2EditPopover>
      ) : null}

      {rolePicker ? (
        <RolePickerPopover
          anchorRect={rolePicker.rect}
          onPick={handleEnableUser}
          onClose={() => setRolePicker(null)}
        />
      ) : null}

      {teamPicker ? (
        <TeamPickerPopover
          anchorRect={teamPicker.rect}
          items={teamsForPicker(
            teamPicker.agent,
            teamPicker.fromTeamId,
            teamPicker.mode
          )}
          onPick={handleTeamPickerSelect}
          onClose={closeTeamPicker}
          ariaLabel={
            teamPicker.mode === 'move'
              ? Number(teamPicker.fromTeamId) > 0
                ? 'Alterar time'
                : 'Definir time'
              : 'Replicar para outro time'
          }
          emptyMessage="Nenhum outro time disponível"
        />
      ) : null}

      {disableConfirm ? (
        <VchConfirmModal
          titleId="vch-disable-agent-title"
          title="Desabilitar agente"
          message={
            <>
              Remover <strong>{agentName(disableConfirm.agent)}</strong> do chat? O vínculo
              será desfeito e a lista atualiza na hora — o usuário passa para{' '}
              <strong>Usuários disponíveis</strong> ou, se ainda restar como agente, para{' '}
              <strong>Sem time</strong>.
            </>
          }
          confirmLabel="Desabilitar"
          confirmingLabel="Desabilitando…"
          confirming={Boolean(busyKey)}
          onConfirm={confirmDisableAgent}
          onCancel={closeDisableConfirm}
        />
      ) : null}

      {deleteTeamConfirm ? (
        <VchConfirmModal
          titleId="vch-delete-team-title"
          title="Remover time"
          message={
            <>
              Remover o time{' '}
              <strong>
                {String(deleteTeamConfirm.team?.name || '').trim() ||
                  `Time #${deleteTeamConfirm.team?.id}`}
              </strong>
              ? Os agentes vinculados deixarão de pertencer a esta fila.
            </>
          }
          confirmLabel="Remover"
          confirmingLabel="Removendo…"
          confirming={Boolean(busyKey)}
          onConfirm={confirmDeleteTeam}
          onCancel={closeDeleteTeamConfirm}
        />
      ) : null}

      <TeamEditPopover
        open={!!editTeam}
        anchorRef={editAnchorRef}
        team={editTeam}
        onClose={closeTeamEdit}
        onSaved={load}
        processEnabled={preProcessEnabled}
        preAtendimentoTeamId={preAtendimentoTeamId}
        contractsTeamId={contractsTeamId}
      />

      <TeamCreatePopover
        open={createOpen}
        anchorRef={createAnchorRef}
        onClose={closeTeamCreate}
        onSaved={load}
        processEnabled={preProcessEnabled}
        preAtendimentoTeamId={preAtendimentoTeamId}
        contractsTeamId={contractsTeamId}
      />
    </section>
  );
}
